import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ControlsIntro.css';

interface ControlsIntroProps {
  open: boolean;
  onClose: () => void;
}

const CONTROLS = [
  { icon: '?', label: 'Rules', desc: 'View the game rules' },
  { icon: '🔊', label: 'Sound', desc: 'Toggle sound on/off' },
  { icon: '⇅ Suit', label: 'Sort', desc: 'Cycle card sorting: by Suit, Rank, or Off' },
  { icon: '✓', label: 'Confirm', desc: 'Toggle tap-to-confirm before playing a card (great for mobile)' },
];

const ControlsIntro: React.FC<ControlsIntroProps> = ({ open, onClose }) => {
  const titleId = React.useId();

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="controls-intro-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
        >
          <motion.div
            className="controls-intro-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30, delay: 0.05 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="controls-intro-header">
              <h3 id={titleId}>Game Controls</h3>
              <span className="controls-intro-sub">Top-left buttons during gameplay</span>
            </div>
            <div className="controls-intro-list">
              {CONTROLS.map((c) => (
                <div className="controls-intro-item" key={c.label}>
                  <span className="controls-intro-icon">{c.icon}</span>
                  <div className="controls-intro-text">
                    <span className="controls-intro-label">{c.label}</span>
                    <span className="controls-intro-desc">{c.desc}</span>
                  </div>
                </div>
              ))}
            </div>
            <button className="controls-intro-dismiss" onClick={onClose}>
              Got it!
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ControlsIntro;
